import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpLeft, ArrowUpRight, ArrowDown, ArrowUp, Play, Loader2, Eye, EyeOff } from "lucide-react";
import { useLang, LOCALES } from "@/lib/i18n";
import { api, fileUrl, imgUrl } from "@/lib/api";
import { sendReelEvent } from "@/lib/reels";
import { useSiteLogo } from "@/lib/useSiteLogo";
import ReelVideo, { pickQuality, bufferedAhead } from "@/components/reels/ReelVideo";
import ReelActions from "@/components/reels/ReelActions";
import { whenQuiet } from "@/lib/netGate";
import { isBufferedToEnd } from "@/lib/videoPlayback";
import { sendQualifiedView } from "@/lib/views";

const WARM_AFTER_AHEAD_SEC = 4;

const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const MUTE_KEY = "reels_muted_v2";   // localStorage: the visitor's mute choice survives clips, pages and sessions
const DETAILS_KEY = "reels_details_v1";

/** Poster-only neighbour that peeks in while dragging (no second <video> is ever loaded). */
const GDEBUG = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "1";
const goodConnection = () => { const c = navigator.connection; return !(c?.saveData) && !["slow-2g", "2g"].includes(c?.effectiveType || ""); };

function PeekSlide({ reel, lang, offsetPct }) {
    const poster = (reel.video_poster_url || reel.cover_image) ? imgUrl(reel.video_poster_url || reel.cover_image, 640) : null;
    const title = (lang !== "ar" && reel.translation?.title) || reel.title;
    return (
        <div aria-hidden="true" className="absolute inset-0 overflow-hidden bg-[#11191c]" style={{ transform: `translateY(${offsetPct}%)` }} data-testid="reel-peek">
            {poster && <div className="absolute -inset-4 bg-cover bg-center" style={{ backgroundImage: `url("${poster}")`, filter: "blur(18px) brightness(.55)" }} />}
            {poster && <img src={poster} alt="" className="absolute inset-0 h-full w-full object-contain" />}
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,#050d1570 0%,transparent 25%,transparent 47%,#071114aa 70%,#071114f7 100%)" }} />
            <span className="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 flex h-[65px] w-[65px] items-center justify-center rounded-full border border-white/40 bg-[#06101450] text-white"><Play className="w-[30px] h-[30px] ms-1" /></span>
            <div className="absolute inset-x-[17px] bottom-[61px] m-0 text-[19px] leading-[1.65] font-display font-extrabold text-white line-clamp-3">{title}</div>
            {/* No hidden <video> here: the next clip's first bytes are fetched by ONE cancellable range request in ReelsFeed,
                and only after the playing clip has a comfortable buffer. */}
        </div>
    );
}

/** Byline (owner photo + name + date), expandable title and a compact «read the story» pill. Used inside the player (overlay) and
 *  in the desktop side column (static). */
function Story({ reel, lang, t, p, logo, suppressClick, overlay, hint }) {
    const [expanded, setExpanded] = useState(false);
    useEffect(() => setExpanded(false), [reel.id]);
    const title = (lang !== "ar" && reel.translation?.title) || reel.title;
    const date = reel.published_at ? new Date(reel.published_at).toLocaleDateString(LOCALES[lang] || "ar-EG-u-nu-latn", { day: "numeric", month: "long", year: "numeric" }) : "";
    const ArrowIcon = lang !== "ar" ? ArrowUpRight : ArrowUpLeft;
    const long = (title || "").length > 70;
    return (
        <div data-testid="reel-story" className={overlay ? "" : "text-white"}>
            <div className="flex items-center gap-2 mb-2.5">
                {logo ? <img src={fileUrl(logo)} alt="" className={`${overlay ? "h-[32px] w-[32px]" : "h-11 w-11"} rounded-full border border-white/40 object-cover bg-white`} data-testid="reel-author-photo" /> : <span className={`${overlay ? "h-[32px] w-[32px]" : "h-11 w-11"} rounded-full border border-white/40 bg-brand-red`} />}
                <div><div className={`${overlay ? "text-xs" : "text-sm"} font-bold leading-[1.5]`}>{t("author")}</div>{date && <div className={`${overlay ? "text-[10px]" : "text-xs"} text-[#d3dddf] leading-[1.5]`} data-testid="reel-date">{date}</div>}</div>
            </div>
            <h2 className={`m-0 mb-2 ${overlay ? "text-[17px]" : "text-[20px]"} leading-[1.65] font-display font-extrabold text-white ${expanded ? "max-h-[38vh] overflow-y-auto pe-1" : "line-clamp-2"}`} style={{ textWrap: "balance" }} data-testid="reel-title" data-expanded={expanded}>{title}</h2>
            <div className="flex items-center gap-2 flex-wrap">
                {long && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); if (Date.now() < suppressClick.current) return; setExpanded((x) => !x); }} className="text-[12px] font-bold text-[#f0b44c] min-h-[32px] px-1" data-testid="reel-title-toggle">{expanded ? t("showLess") : t("showMore")}</button>
                )}
                <Link to={`${p}/article/${reel.slug}`} onClick={(e) => { if (Date.now() < suppressClick.current) e.preventDefault(); }} data-testid="reel-read-button"
                    className="inline-flex min-h-[36px] items-center gap-2 rounded-full bg-brand-red px-3.5 py-1.5 text-xs font-bold text-white no-underline hover:bg-[#b51f35]">
                    <span>{t("readStory")}</span><ArrowIcon className="w-4 h-4" />
                </Link>
                {hint}
            </div>
        </div>
    );
}

/** Interaction (confirmed by real-device traces 2026-10-09): the visitor's natural "next" swipe moves the finger UP ↑
 *  (content flows upward, TikTok-style) → next clip (current exits up, next rises from below); finger DOWN ↓ → previous.
 *  Wheel down / ArrowDown → next; wheel up / ArrowUp → previous.
 *  The whole track follows the finger, the neighbour clip peeks in, then settles; a short drag snaps back.
 *  Gestures are captured inside the player only — the page scrolls normally outside it. `side` renders details/actions in a
 *  column next to the video (desktop) instead of over it. */
export default function ReelsFeed({ items, onItemChange, index, onIndexChange, source, className = "", autoFocus = false, footer = true, onNeedMore, hasMore = false, fill = false, side = false }) {
    const { t, lang, p } = useLang();
    const [muted, setMutedState] = useState(() => { try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; } });   // sound ON unless the visitor muted it
    const setMuted = (m) => { setMutedState(m); try { localStorage.setItem(MUTE_KEY, m ? "1" : "0"); } catch { /* noop */ } };
    const [details, setDetailsState] = useState(() => { try { return sessionStorage.getItem(DETAILS_KEY) !== "0"; } catch { return true; } });
    const setDetails = (d) => { setDetailsState(d); try { sessionStorage.setItem(DETAILS_KEY, d ? "1" : "0"); } catch { /* noop */ } };
    const [toastMsg, setToastMsg] = useState("");
    const logo = useSiteLogo();
    const [sheetOpen, setSheetOpen] = useState(false);
    const rootRef = useRef(null);
    const trackRef = useRef(null);
    const gesture = useRef(null);
    const moving = useRef(false);
    const suppressClick = useRef(0);
    const wheel = useRef({ total: 0, at: 0, lock: 0 });
    const toastTimer = useRef(null);
    const viewed = useRef(new Set());
    const warmed = useRef(new Set());
    const n = items.length;
    const reel = items[index];
    const prev = items[index - 1], next = items[index + 1];
    useEffect(() => { if (onNeedMore && hasMore && index >= n - 4) onNeedMore(); }, [index, n, onNeedMore, hasMore]);   // fetch the next batch well before the end
    const [fetchingNext, setFetchingNext] = useState(false);
    const pendingNav = useRef(false);
    // Landscape clips in the embedded (article/video page) frame: the frame takes the clip's own aspect ratio and the
    // title/author/actions move to a strip below it — no tall dark bands, no cropping or stretching.
    const [ratio, setRatio] = useState(0);   // width / height when > 1 (landscape), else 0
    const applyDims = useCallback((w, h) => { if (w > 0 && h > 0 && w > h) setRatio(Math.min(1.78, Math.max(1.0, w / h))); }, []);
    useEffect(() => {
        setRatio(0);
        const dims = String(reel?.video_web_dims || "").split("x").map(Number);
        if (dims.length === 2 && dims[0] > 0 && dims[1] > 0) { applyDims(dims[0], dims[1]); return; }
        const src = reel?.video_poster_url || reel?.cover_image; if (!src) return;
        const im = new Image(); im.onload = () => applyDims(im.naturalWidth, im.naturalHeight); im.src = fileUrl(src);
        return () => { im.onload = null; };
    }, [reel?.id, reel?.video_web_dims, reel?.video_poster_url, reel?.cover_image, applyDims]);
    const landscapeFrame = !fill && !side && ratio > 0;
    // Warm the START of the next clip (first ~384 KB, ONE HTTP range) only once the playing clip has ≥ WARM_AFTER_AHEAD_SEC
    // buffered ahead (or is fully buffered) — the current clip always wins the link. The request is aborted the moment the
    // visitor moves to another clip, so no stale bytes keep flowing.
    useEffect(() => {
        if (!next || !goodConnection()) return;
        const url = fileUrl((pickQuality() === "low" ? next.video_low_url : next.video_web_url) || next.video_web_url || next.video_url);
        if (!url || warmed.current.has(url)) return;
        const ctrl = new AbortController();
        let fired = false;
        const iv = setInterval(() => {
            const v = rootRef.current?.querySelector("[data-testid=reel-video]");
            if (!v || fired) return;
            const ready = v.ended || bufferedAhead(v) >= WARM_AFTER_AHEAD_SEC || isBufferedToEnd(v);
            if (!ready) return;
            fired = true; clearInterval(iv); warmed.current.add(url);
            fetch(url, { headers: { Range: "bytes=0-393215" }, signal: ctrl.signal, priority: "low" }).then((r) => r.arrayBuffer()).catch(() => { warmed.current.delete(url); });
        }, 500);
        return () => { clearInterval(iv); ctrl.abort(); };
    }, [next]);   // eslint-disable-line react-hooks/exhaustive-deps

    const showToast = useCallback((msg) => {
        clearTimeout(toastTimer.current);
        setToastMsg(msg);
        toastTimer.current = setTimeout(() => setToastMsg(""), 2300);
    }, []);
    const setOffset = (px, animate) => {
        const tr = trackRef.current; if (!tr) return;
        tr.style.transition = animate && !reduced() ? `transform ${animate}ms cubic-bezier(.2,.7,.25,1)` : "none";
        tr.style.transform = `translateY(${px}px)`;
    };
    const height = () => rootRef.current?.getBoundingClientRect().height || 600;

    /** Slide the track a full screen in the drag direction (down = next, up = previous), then commit the new index. */
    const navigate = useCallback((step) => {
        if (moving.current || sheetOpen) return Promise.resolve();
        const target = index + step;
        if (target >= n && hasMore && onNeedMore) {   // batch boundary: show a loader, fetch, then complete THIS swipe automatically
            setOffset(0, 160); setFetchingNext(true); pendingNav.current = true;
            return Promise.resolve(onNeedMore()).finally(() => setFetchingNext(false));
        }
        if (target < 0 || target >= n) { setOffset(0, 160); showToast(target < 0 ? t("reelsFirstClip") : t("reelsLastClip")); return Promise.resolve(); }
        moving.current = true;
        suppressClick.current = Date.now() + 550;
        const h = height(), dir = step > 0 ? -1 : 1;   // next → current exits UP, the clip below (next) rises in
        const dur = reduced() ? 0 : 220;
        setOffset(dir * h, dur);
        return new Promise((res) => setTimeout(res, dur)).then(() => {
            onIndexChange(target);
            setOffset(0, 0);
            moving.current = false;
        });
    }, [index, n, sheetOpen, onIndexChange, showToast, t, hasMore, onNeedMore]);
    useEffect(() => { if (pendingNav.current && index < n - 1) { pendingNav.current = false; navigate(1); } }, [n]);   // eslint-disable-line react-hooks/exhaustive-deps

    const begin = (x, y) => {
        if (moving.current || sheetOpen) return;
        gesture.current = { x, y, t: Date.now(), dragging: false };
    };
    const move = (x, y) => {
        const g = gesture.current; if (!g || moving.current) return false;
        const dy = y - g.y, dx = x - g.x;
        if (!g.dragging && (Math.abs(dy) <= 10 || Math.abs(dy) <= Math.abs(dx))) return false;
        g.dragging = true;
        suppressClick.current = Date.now() + 450;   // a drag is in progress: taps on like/save must not fire
        const target = index + (dy < 0 ? 1 : -1);
        const h = height();
        const px = target < 0 || target >= n ? dy * 0.16 : Math.max(-h, Math.min(h, dy));   // rubber-band at the edges
        setOffset(px, 0);
        return true;
    };
    const end = (x, y) => {
        const g = gesture.current; if (!g) return;
        gesture.current = null;
        if (!g.dragging) return;
        const dy = y - g.y, dx = x - g.x;
        suppressClick.current = Date.now() + 450;
        const threshold = Math.max(40, height() * 0.12);
        const speed = Math.abs(dy) / Math.max(1, Date.now() - g.t);   // px/ms — a quick natural flick commits even if short
        const commit = Math.abs(dy) > Math.abs(dx) && (Math.abs(dy) > threshold || (speed > 0.45 && Math.abs(dy) > 24));
        const decision = commit ? (dy < 0 ? `next (dy=${Math.round(dy)})` : `prev (dy=${Math.round(dy)})`) : `none (dy=${Math.round(dy)} dx=${Math.round(dx)})`;
        log(`end ${decision}`); flushTrace(decision);
        if (commit) navigate(dy < 0 ? 1 : -1); else setOffset(0, 180);
    };
    const cancel = () => { gesture.current = null; if (!moving.current) setOffset(0, 180); };

    // One gesture path for finger, mouse and pen: Pointer Events (every modern mobile browser fires them for touch).
    // Native Touch Events are used only as a fallback where PointerEvent is missing, plus a non-passive touchmove that
    // keeps iOS Safari from turning the swipe into a page scroll. The seek bar and links are excluded.
    const GESTURE_EXCLUDE = "a,input,button[data-testid=reel-title-toggle],[role=dialog]";
    const HAS_PE = typeof window !== "undefined" && "PointerEvent" in window;
    const [dbg, setDbg] = useState("");
    // Trace buffer: every gesture event is recorded; on touch devices the first few gestures per session are POSTed
    // anonymously (/api/public/reels/trace) so real-device sequences can be inspected without a debugger.
    const trace = useRef([]);
    const traceSent = useRef(0);
    const flushTrace = (decision) => {
        const evs = trace.current; trace.current = [];
        const touch = evs.some((e) => /touch/.test(e));
        if (!touch || traceSent.current >= 6) return;
        traceSent.current += 1;
        whenQuiet(() => api.post("/public/reels/trace", { ua: navigator.userAgent.slice(0, 300), page: window.location.pathname.slice(0, 200), viewport: `${window.innerWidth}x${window.innerHeight}`,
            events: evs.slice(-60), decision, index_before: index, index_after: index }).catch(() => {}));
    };
    const log = (msg) => {
        trace.current.push(`${Date.now() % 100000} ${msg}`.slice(0, 60));
        if (GDEBUG) setDbg(`${new Date().toISOString().slice(11, 23)} ${msg}`);
    };
    const onPointerDown = (e) => {
        log(`down ${e.pointerType} primary=${e.isPrimary} tgt=${e.target.dataset?.testid || e.target.tagName}`);
        if (!HAS_PE || !e.isPrimary || (e.pointerType === "mouse" && e.button !== 0) || e.target.closest(GESTURE_EXCLUDE)) return;
        if (e.pointerType === "mouse") rootRef.current?.focus({ preventScroll: true });
        begin(e.clientX, e.clientY);
        if (gesture.current) { gesture.current.pid = e.pointerId; gesture.current.lx = e.clientX; gesture.current.ly = e.clientY; }
    };
    const onPointerMove = (e) => {
        const g = gesture.current; if (!HAS_PE || !g || g.viaTouch || e.pointerId !== g.pid) return;
        g.lx = e.clientX; g.ly = e.clientY;
        log(`move dy=${Math.round(e.clientY - g.y)} dragging=${g.dragging}`);
        if (move(e.clientX, e.clientY)) { const el = rootRef.current; try { if (el && !el.hasPointerCapture(e.pointerId)) el.setPointerCapture(e.pointerId); } catch { /* noop */ } }
    };
    const onPointerUp = (e) => {
        const g = gesture.current; if (!HAS_PE || !g || g.viaTouch || e.pointerId !== g.pid) return;
        const el = rootRef.current; try { if (el?.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId); } catch { /* noop */ }
        log(`up ${e.pointerType} dy=${Math.round(e.clientY - g.y)} idx=${index}`);
        end(e.clientX, e.clientY);
    };
    const onPointerCancel = (e) => {
        const g = gesture.current; if (!HAS_PE || !g || g.viaTouch || e.pointerId !== g.pid) return;
        // The browser took the pointer (pull-to-refresh / rubber-band / native pan). Touch events keep flowing after
        // pointercancel, so hand the SAME gesture over to the touch path instead of dropping it.
        log(`CANCEL dy=${Math.round(g.ly - g.y)} dragging=${g.dragging} → touch path`);
        if (e.pointerType === "touch") { g.viaTouch = true; return; }
        if (g.dragging) end(g.lx, g.ly); else { log("cancel → dropped"); flushTrace("cancel"); cancel(); }
    };
    const H = useRef({}); H.current = { begin, move, end, cancel, log };   // always-fresh handlers for the native listeners below
    useEffect(() => {
        const el = rootRef.current; if (!el) return;
        let touchOrigin = null;   // hybrid safety net: if a finger moves but no pointerdown ever arrived, drive the gesture from touch events
        const ts = (e) => {
            if (e.touches.length !== 1 || e.target.closest(GESTURE_EXCLUDE)) { touchOrigin = null; return; }
            touchOrigin = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            H.current.log?.(`touchstart y=${Math.round(touchOrigin.y)} tgt=${e.target.dataset?.testid || e.target.tagName}`);
            if (!HAS_PE) H.current.begin(touchOrigin.x, touchOrigin.y);
        };
        const tm = (e) => {
            if (e.touches.length !== 1) return;
            const tx = e.touches[0].clientX, ty = e.touches[0].clientY;
            if (!gesture.current && touchOrigin) { H.current.begin(touchOrigin.x, touchOrigin.y); if (gesture.current) { gesture.current.viaTouch = true; H.current.log?.("touch fallback engaged"); } }
            const g = gesture.current; if (!g) { H.current.log?.(`touchmove no-gesture y=${Math.round(ty)}`); return; }
            if (g.viaTouch) { g.lx = tx; g.ly = ty; H.current.log?.(`touchmove via-touch dy=${Math.round(ty - g.y)}`); H.current.move(tx, ty); }
            if (Math.abs(ty - g.y) >= Math.abs(tx - g.x)) { if (e.cancelable) e.preventDefault(); else H.current.log?.(`touchmove NOT cancelable dy=${Math.round(ty - g.y)}`); }   // claim the vertical swipe before any native scroll
            else if (!HAS_PE) H.current.move(tx, ty);
        };
        const te = (e) => { const g = gesture.current; touchOrigin = null; H.current.log?.(`touchend g=${!!g} via=${!!g?.viaTouch}`); if (!g || (HAS_PE && !g.viaTouch)) return; const t0 = e.changedTouches[0]; if (t0) H.current.end(t0.clientX, t0.clientY); };
        const tc = () => { const g = gesture.current; touchOrigin = null; H.current.log?.(`touchcancel g=${!!g} via=${!!g?.viaTouch} drag=${!!g?.dragging}`); if (!g || (HAS_PE && !g.viaTouch)) return; if (g.dragging) H.current.end(g.lx, g.ly); else H.current.cancel(); };
        el.addEventListener("touchstart", ts, { passive: true });
        el.addEventListener("touchmove", tm, { passive: false });
        el.addEventListener("touchend", te, { passive: true });
        el.addEventListener("touchcancel", tc, { passive: true });
        return () => { el.removeEventListener("touchstart", ts); el.removeEventListener("touchmove", tm); el.removeEventListener("touchend", te); el.removeEventListener("touchcancel", tc); };
    }, []);   // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {   // native (non-passive) wheel so preventDefault keeps the page still while over the player
        const el = rootRef.current; if (!el) return;
        const onWheel = (e) => {
            if (e.ctrlKey || sheetOpen || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
            e.preventDefault();
            const w = wheel.current, now = Date.now();
            if (moving.current || now < w.lock) return;
            const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.getBoundingClientRect().height : 1;
            if (now - w.at > 180 || Math.sign(w.total) !== Math.sign(e.deltaY)) w.total = 0;
            w.total += e.deltaY * unit; w.at = now;
            if (Math.abs(w.total) >= 45) { const dir = Math.sign(w.total); w.total = 0; w.lock = now + 650; navigate(dir); }
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [navigate, sheetOpen]);

    useEffect(() => { if (autoFocus) rootRef.current?.focus({ preventScroll: true }); }, [autoFocus]);
    const onKey = (e) => {
        if (sheetOpen || e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
        if (e.key === "ArrowDown" || e.key === "PageDown") { e.preventDefault(); navigate(1); }
        else if (e.key === "ArrowUp" || e.key === "PageUp") { e.preventDefault(); navigate(-1); }
        else if (e.key === " " || e.key === "Spacebar" || e.key === "k" || e.key === "K") {   // Space/K: pause ↔ resume the clip at the same second; no page scroll
            if (e.target.tagName === "BUTTON" || e.target.tagName === "A") return;   // let focused buttons/links keep their native Space behaviour
            e.preventDefault();
            rootRef.current?.querySelector("[data-testid=reel-gesture-layer]")?.click();
        }
    };
    const onViewed = (r, sec) => { if (viewed.current.has(r.id)) return; viewed.current.add(r.id); sendReelEvent("view", { articleId: r.id, source, lang }); sendQualifiedView(r.id, "video", sec); };

    if (!reel) return null;
    const title = (lang !== "ar" && reel.translation?.title) || reel.title;
    const num = (x) => new Intl.NumberFormat("en-US", { useGrouping: false }).format(x);
    const last = index === n - 1;
    const hint = fill && index === 0 && n > 1 ? <span className="inline-flex items-center gap-1.5 rounded-full bg-black/45 px-2.5 py-1 text-[11px] font-semibold text-[#e3e8e9]" data-testid="reel-inline-hint">{t("reelsSwipeHint")} <ArrowUp className="w-3.5 h-3.5 text-brand-red" /></span> : null;
    const detailsBtn = (
        <button type="button" onClick={(e) => { e.stopPropagation(); if (Date.now() < suppressClick.current) return; setDetails(!details); }} aria-pressed={!details} aria-label={details ? t("hideDetails") : t("showDetails")} data-testid="reel-toggle-details"
            style={{ top: "calc(13px + var(--reel-safe-t, 0px))" }} className="absolute start-[14px] z-[4] h-[43px] rounded-full border border-white/20 bg-[#07131866] text-white flex items-center gap-1.5 px-3 backdrop-blur-md text-[11px] font-semibold">
            {details ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}<span>{details ? t("hideDetails") : t("showDetails")}</span>
        </button>
    );

    const player = (
        <div ref={rootRef} tabIndex={0} onKeyDown={onKey} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}
            role="region" aria-label={t("reels")} aria-roledescription="reels"
            className={`relative w-full bg-[#11191c] text-white overflow-hidden select-none outline-none focus-visible:ring-2 focus-visible:ring-[#f0b44c] ${fill ? "h-full" : "rounded-t-lg"}`}
            style={{ ...(fill ? {} : { aspectRatio: landscapeFrame ? `${ratio} / 1` : "9 / 16" }), touchAction: "none", WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none", WebkitTapHighlightColor: "transparent", overscrollBehavior: "contain", isolation: "isolate", WebkitUserSelect: "none", WebkitTouchCallout: "none" }} data-testid="reels-feed" data-index={index} data-reel-id={reel.id} data-details={details} data-landscape={landscapeFrame}>
            <div ref={trackRef} className="absolute inset-0 will-change-transform" data-testid="reels-track">
                {next && <PeekSlide reel={next} lang={lang} offsetPct={100} />}
                {prev && <PeekSlide reel={prev} lang={lang} offsetPct={-100} />}
                <div className="absolute inset-0 overflow-hidden" style={{ isolation: "isolate" }} data-testid="reel-slide">
                    <ReelVideo key={reel.id} reel={reel} active muted={muted} onMutedChange={setMuted} onViewed={onViewed} suppressClickRef={suppressClick} chrome={details || side} landscapeFrame={landscapeFrame} onMeta={applyDims} />
                    {!side && !landscapeFrame && <ReelActions reel={reel} source={source} onLikeChange={onItemChange} onSheet={setSheetOpen} suppressClickRef={suppressClick} hidden={!details} />}
                    {!side && !landscapeFrame && (
                        <div className={`absolute inset-x-[17px] end-[72px] z-[2] transition-[opacity,transform] duration-200 ${details ? "" : "opacity-0 translate-y-3 pointer-events-none"}`} style={{ bottom: "calc(70px + var(--reel-safe-b, 0px))" }} aria-hidden={!details}>
                            <Story reel={reel} lang={lang} t={t} p={p} logo={logo} suppressClick={suppressClick} overlay hint={hint} />
                        </div>
                    )}
                </div>
            </div>
            {!side && !landscapeFrame && detailsBtn}
            {GDEBUG && <div dir="ltr" className="absolute start-2 z-[5] text-[10px] font-mono text-white bg-black/75 px-2 py-1 rounded pointer-events-none" style={{ top: "calc(110px + var(--reel-safe-t, 0px))" }} data-testid="gesture-debug">PE={String(HAS_PE)} idx={index}/{n} {dbg}</div>}
            {fetchingNext && <div role="status" className="absolute inset-x-0 z-[8] flex justify-center pointer-events-none" style={{ bottom: "calc(40px + var(--reel-safe-b, 0px))" }} data-testid="reel-fetching-next"><span className="inline-flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold"><Loader2 className="w-4 h-4 animate-spin" /> {t("videoLoading")}</span></div>}
            {toastMsg && <div role="status" style={{ top: "calc(74px + var(--reel-safe-t, 0px))" }} className="absolute inset-x-[17px] z-[8] rounded-[10px] border border-white/20 bg-[#152b2fea] px-3 py-2.5 text-center text-xs shadow-lg" data-testid="reel-toast">{toastMsg}</div>}
            <span className="sr-only" aria-live="polite">{t("clipAnnounce", num(index + 1), title)}</span>
        </div>
    );

    if (side) {
        return (
            <div className={`flex items-stretch gap-6 ${lang !== "ar" ? "flex-row-reverse" : ""} ${className}`} data-testid="reels-shell" data-layout="side">
                <aside className="w-[300px] shrink-0 flex flex-col justify-end gap-6 py-2" data-testid="reels-side">
                    <div className="flex items-center gap-2 self-start">
                        <button type="button" onClick={() => navigate(-1)} disabled={index === 0} aria-label={t("prevVideo")} className="h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white flex items-center justify-center" data-testid="reels-nav-prev"><ArrowUp className="w-5 h-5" /></button>
                        <button type="button" onClick={() => navigate(1)} disabled={last && !hasMore} aria-label={t("nextVideo")} className="h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white flex items-center justify-center" data-testid="reels-nav-next"><ArrowDown className="w-5 h-5" /></button>
                        <span className="text-[11px] text-gray-400 inline-flex items-center gap-1">{t("reelsSwipeHint")} <ArrowUp className="w-3.5 h-3.5 text-brand-red" /></span>
                    </div>
                    <Story reel={reel} lang={lang} t={t} p={p} logo={logo} suppressClick={suppressClick} />
                    <div className="self-start -ms-2"><ReelActions reel={reel} source={source} onLikeChange={onItemChange} onSheet={setSheetOpen} suppressClickRef={suppressClick} variant="side" /></div>
                </aside>
                <div className="h-full shrink-0 rounded-xl overflow-hidden shadow-[0_30px_80px_#00000080]" style={{ aspectRatio: "9 / 16" }}>{player}</div>
            </div>
        );
    }

    return (
        <div className={className} data-testid="reels-shell">
            {player}
            {landscapeFrame && (
                <div className="bg-[#11191c] text-white px-4 pt-3 pb-2 flex flex-col gap-2" data-testid="reel-landscape-details">
                    <Story reel={reel} lang={lang} t={t} p={p} logo={logo} suppressClick={suppressClick} hint={hint} />
                    <ReelActions reel={reel} source={source} onLikeChange={onItemChange} onSheet={setSheetOpen} suppressClickRef={suppressClick} variant="row" />
                </div>
            )}
            {footer && (
                <div className="flex min-h-[40px] items-center justify-center gap-[7px] rounded-b-lg bg-white dark:bg-[#1a292c] px-3 py-2.5 text-[11px] font-semibold text-gray-500 dark:text-gray-300 border border-t-0 border-gray-200 dark:border-gray-700" data-testid="reel-swipe-footer">
                    <span>{last ? t("reelsSwipeUpBack") : t("reelsSwipeHint")}</span>{last ? <ArrowDown className="w-4 h-4 text-brand-red" /> : <ArrowUp className="w-4 h-4 text-brand-red" />}
                </div>
            )}
        </div>
    );
}
